import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { useMarketplaceConfig } from '../queries/marketplace';
import { SkinContextValue, SkinOptionsConfig } from '../types/skin';
import { FEATURE_FLAG } from '../constants/featureFlags';
import { createCustomTheme } from '../styles/theme';
import { getDefaultSkinConfig, resolveSkinConfig } from '../styles/themeUtils';
import FullScreenLoader from '../components/atoms/FullScreenLoader';
import { useGoogleFonts } from '../hooks/useGoogleFonts';

const SkinContext = createContext<SkinContextValue | undefined>(undefined);

export const useSkin = (): SkinContextValue => {
  const context = useContext(SkinContext);

  if (!context) {
    throw new Error('useSkin must be used within a SkinProvider');
  }
  return context;
};

export const SkinProvider = ({ children }: PropsWithChildren) => {
  const { data: marketplaceConfig, isLoading: isConfigLoading } = useMarketplaceConfig();
  const skinOptions = useFeatureValue<SkinOptionsConfig>(FEATURE_FLAG.SKIN_OPTIONS, []);

  const skinConfig = useMemo(() => {
    if (!marketplaceConfig) {
      return getDefaultSkinConfig();
    }

    const resolved = resolveSkinConfig(marketplaceConfig.domains);

    if (skinOptions.length > 0) {
      const isAllowed = skinOptions.some((opt) => opt.id === resolved.skin);

      if (!isAllowed) {
        return getDefaultSkinConfig();
      }
    }

    return resolved;
  }, [marketplaceConfig, skinOptions]);

  useGoogleFonts(skinConfig.skin, skinConfig.theme);

  const muiTheme = useMemo(
    () => createCustomTheme(skinConfig.skin, skinConfig.theme),
    [skinConfig.skin, skinConfig.theme]
  );

  const value: SkinContextValue = useMemo(
    () => ({ ...skinConfig, isLoading: isConfigLoading }),
    [skinConfig, isConfigLoading]
  );

  if (!muiTheme) {
    return <FullScreenLoader />;
  }

  return (
    <SkinContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </SkinContext.Provider>
  );
};
